/**
 * Viewport maths — screen space <-> canvas space.
 *
 * This module is the ONLY place these conversions are implemented.
 * No module may write its own version (docs/04-MODULE-CONTRACTS.md §3.3.1, §10.5).
 *
 * Coordinate model
 * ----------------
 *   screen = (canvas - pan) * zoom
 *   canvas = screen / zoom + pan
 *
 * So `panX` / `panY` are the CANVAS coordinates currently at the top-left corner of
 * the visible viewport, and `zoom` is screen pixels per canvas unit.
 */
/** SRS C2 — zoom range. */
export const MIN_ZOOM = 0.1;
export const MAX_ZOOM = 8.0;
/** SRS C3 — rendered stroke width is clamped to these screen-pixel bounds. */
export const MIN_RENDERED_WIDTH_PX = 0.5;
export const MAX_RENDERED_WIDTH_PX = 60;
/** SRS C3 — the thickness slider works in target SCREEN pixels. */
export const MIN_TARGET_WIDTH_PX = 1;
export const MAX_TARGET_WIDTH_PX = 20;
export const DEFAULT_TARGET_WIDTH_PX = 3;
/** Restrict a number to a range. */
export function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
}
/** Restrict a zoom level to the supported 10%–800% range. */
export function clampZoom(zoom) {
    return clamp(zoom, MIN_ZOOM, MAX_ZOOM);
}
/** Convert a point from screen pixels to canvas units. */
export function screenToCanvas(viewport, p) {
    return {
        x: p.x / viewport.zoom + viewport.panX,
        y: p.y / viewport.zoom + viewport.panY,
    };
}
/** Convert a point from canvas units to screen pixels. */
export function canvasToScreen(viewport, p) {
    return {
        x: (p.x - viewport.panX) * viewport.zoom,
        y: (p.y - viewport.panY) * viewport.zoom,
    };
}
/**
 * Canvas-space width that renders as the given screen width at the current zoom.
 *
 * This is the function that produces `Stroke.canvasWidth` (SRS C3). The pen tool holds
 * a target SCREEN width and calls this every time a stroke starts, so the ink always
 * feels the same thickness under the hand no matter how far the teacher has zoomed.
 */
export function screenWidthToCanvasWidth(viewport, screenPx) {
    return screenPx / viewport.zoom;
}
/** Screen width a stored canvas-space width renders as at the current zoom. */
export function canvasWidthToScreenWidth(viewport, canvasPx) {
    return canvasPx * viewport.zoom;
}
/**
 * The actual width to stroke with, in screen pixels, clamped so that a stroke is never
 * invisible when zoomed far out and never a solid slab when zoomed far in (SRS C3).
 */
export function renderedStrokeWidth(viewport, canvasWidth) {
    return clamp(canvasWidthToScreenWidth(viewport, canvasWidth), MIN_RENDERED_WIDTH_PX, MAX_RENDERED_WIDTH_PX);
}
/**
 * The number shown beside the thickness slider (SRS C3).
 *
 * The slider thumb itself never moves when zoom changes — it represents the target
 * screen width, which has not changed. It is this readout that moves: 3px at 100%,
 * 1px at 300%, 6px at 50%. That is intended behaviour, not a bug to be "fixed".
 */
export function thicknessReadout(targetScreenPx, zoom) {
    const canvasWidth = targetScreenPx / zoom;
    const rounded = Math.round(canvasWidth * 10) / 10;
    return Number.isInteger(rounded) ? `${rounded}px` : `${rounded.toFixed(1)}px`;
}
/**
 * Zoom while keeping the canvas point under `screenAnchor` exactly where it is.
 *
 * Used by Ctrl+wheel (zoom to cursor) and pinch-to-zoom, where the content under the
 * pointer or between the fingers must not drift.
 */
export function zoomAt(viewport, nextZoom, screenAnchor) {
    const canvasAnchor = screenToCanvas(viewport, screenAnchor);
    const zoom = clampZoom(nextZoom);
    return {
        ...viewport,
        zoom,
        panX: canvasAnchor.x - screenAnchor.x / zoom,
        panY: canvasAnchor.y - screenAnchor.y / zoom,
    };
}
/** Pan by a distance measured in screen pixels. */
export function panByScreen(viewport, dxPx, dyPx) {
    return {
        ...viewport,
        panX: viewport.panX - dxPx / viewport.zoom,
        panY: viewport.panY - dyPx / viewport.zoom,
    };
}
/** The region of canvas currently visible, in canvas units. Used for culling. */
export function visibleCanvasRect(viewport) {
    return {
        x: viewport.panX,
        y: viewport.panY,
        width: viewport.widthPx / viewport.zoom,
        height: viewport.heightPx / viewport.zoom,
    };
}
/** True when two canvas-space rectangles overlap at all. */
export function rectsIntersect(a, b) {
    return (a.x < b.x + b.width && a.x + a.width > b.x && a.y < b.y + b.height && a.y + a.height > b.y);
}
/**
 * Zoom and pan that fit `content` into the viewport with a margin, for "Fit to Content".
 * Returns the viewport unchanged when there is no content to fit.
 */
export function fitToContent(viewport, content, marginPx = 48) {
    if (content.width <= 0 || content.height <= 0)
        return viewport;
    const usableWidth = Math.max(1, viewport.widthPx - marginPx * 2);
    const usableHeight = Math.max(1, viewport.heightPx - marginPx * 2);
    const zoom = clampZoom(Math.min(usableWidth / content.width, usableHeight / content.height));
    const centreX = content.x + content.width / 2;
    const centreY = content.y + content.height / 2;
    return {
        ...viewport,
        zoom,
        panX: centreX - viewport.widthPx / 2 / zoom,
        panY: centreY - viewport.heightPx / 2 / zoom,
    };
}
//# sourceMappingURL=viewport.js.map